import React, { useState } from 'react'
import { useRevenueUnitsDeepDive } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import { BackButton } from '../components/BackButton'
import Badge from '../components/Badge'
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Search,
  Info,
  Package,
  Store,
  Globe,
  AlertTriangle,
  Filter
} from 'lucide-react'

export default function RevenueDetailEnhanced() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('revenue_ty')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({})

  // Data fetching with enhanced deep dive API
  const { data: deepDiveData, isLoading, error, refetch } = useRevenueUnitsDeepDive(
    selectedFiscalWeek,
    'revenue',
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
    50
  )

  const formatCurrency = (value: number | null) => {
    if (value === null) return '—'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatPercent = (value: number | null, decimals = 1) => {
    if (value === null) return '—'
    return `${(value * 100).toFixed(decimals)}%`
  }

  const formatNumber = (value: number | null) => {
    if (value === null) return '—'
    return value.toLocaleString()
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setCurrentPage(1)
  }

  const getRiskBadgeVariant = (flag: string) => {
    switch (flag) {
      case 'negative_units': return 'danger'
      case 'low_distribution': return 'warning'
      case 'oos_risk': return 'danger'
      default: return 'neutral'
    }
  }

  const getRiskBadgeLabel = (flag: string) => {
    switch (flag) {
      case 'negative_units': return 'Returns/Negative'
      case 'low_distribution': return 'Low Distribution'
      case 'oos_risk': return 'OOS Risk'
      default: return flag
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Revenue Deep Dive"
          subtitle="Loading enhanced revenue analysis..."
          isLoading={true}
        />
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="h-96 bg-muted rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Revenue Deep Dive"
          subtitle="Error loading revenue data"
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
          <p className="text-destructive">Failed to load revenue data. Please try again.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const products = deepDiveData?.rows || []
  const totals = deepDiveData?.totals

  return (
    <div className="space-y-6">
      {/* Global Header */}
      <GlobalHeader 
        title="Revenue Deep Dive"
        subtitle="Growth, mix, contribution, and risk analysis by SKU"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      {/* Back Button */}
      <BackButton href="/dashboard" label="Back to Dashboard" className="mb-4" />

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue TY</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(totals?.revenue_ty || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue LY</p>
              <p className="text-xl font-bold text-foreground">
                {totals?.revenue_ly !== null ? formatCurrency(totals.revenue_ly) : (
                  <span className="text-muted-foreground">No Data</span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-sm text-muted-foreground">SKUs Analyzed</p>
              <p className="text-xl font-bold text-foreground">{products.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Store className="w-5 h-5 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">Active Stores</p>
              <p className="text-xl font-bold text-foreground">{deepDiveData?.meta.totalActiveStores || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center space-x-2 max-w-md flex-1">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search products or SKUs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 bg-background border border-border/40 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filters coming soon</span>
        </div>
      </div>

      {/* Enhanced Revenue Analysis Table */}
      <div className="bg-background/80 border border-border/40 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30 border-b border-border/40">
              <tr>
                <th className="sticky left-0 bg-muted/30 text-left p-4 font-medium text-muted-foreground border-r border-border/40">
                  <div className="flex items-center space-x-1">
                    <Package className="w-4 h-4" />
                    <span>Product</span>
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => handleSort('revenue_ty')}>
                  <div className="flex items-center space-x-1">
                    <span>Revenue (TY)</span>
                    {sortField === 'revenue_ty' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => handleSort('rev_yoy_pct')}>
                  <div className="flex items-center space-x-1">
                    <span>YoY Growth %</span>
                    <Info className="w-3 h-3 cursor-help" title="(This week vs same week LY). If LY was zero, 'New vs LY' is shown." />
                    {sortField === 'rev_yoy_pct' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => handleSort('store_share_ty')}>
                  <div className="flex items-center space-x-1">
                    <span>Store Share</span>
                    <Info className="w-3 h-3 cursor-help" title="Store revenue share = (Comp + Non-Comp) ÷ Total revenue" />
                    {sortField === 'store_share_ty' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => handleSort('contribution_rev_pct')}>
                  <div className="flex items-center space-x-1">
                    <span>Contribution</span>
                    <Info className="w-3 h-3 cursor-help" title="SKU revenue as a share of page total this week" />
                    {sortField === 'contribution_rev_pct' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <span>Dotcom $</span>
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <span>Store $</span>
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                    onClick={() => handleSort('oos_pct')}>
                  <div className="flex items-center space-x-1">
                    <span>OOS %</span>
                    <Info className="w-3 h-3 cursor-help" title="Store out-of-stock percentage" />
                    {sortField === 'oos_pct' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <span>Store Count</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.sku} className="border-b border-border/20 hover:bg-muted/20 transition-colors">
                  {/* Product Name (Sticky) */}
                  <td className="sticky left-0 bg-background/90 backdrop-blur-sm p-4 border-r border-border/40">
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-foreground truncate">{product.name}</div>
                        <div className="text-xs text-muted-foreground">{product.sku}</div>
                        {/* Risk Flags */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {product.flags.map((flag) => (
                            <Badge key={flag} variant={getRiskBadgeVariant(flag)} size="sm">
                              {getRiskBadgeLabel(flag)}
                            </Badge>
                          ))}
                          {product.rev_yoy_pct === null && product.revenue_ly === 0 && (
                            <Badge variant="neutral" size="sm">New vs LY</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Revenue (TY) */}
                  <td className="p-4">
                    <div className="font-medium text-foreground">{formatCurrency(product.revenue_ty)}</div>
                  </td>

                  {/* YoY Growth % */}
                  <td className="p-4">
                    {product.rev_yoy_pct !== null ? (
                      <Badge 
                        variant={product.rev_yoy_pct > 0 ? 'success' : product.rev_yoy_pct < 0 ? 'danger' : 'neutral'}
                        showTrend={true}
                        trendDirection={product.rev_yoy_pct > 0 ? 'up' : 'down'}
                      >
                        {formatPercent(product.rev_yoy_pct)}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Store Share with sparkline placeholder */}
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">{formatPercent(product.store_share_ty, 0)}</div>
                        {/* Placeholder for sparkline - would show channel mix trend */}
                        <div className="w-16 h-2 bg-muted/30 rounded-full mt-1">
                          <div 
                            className="h-2 bg-orange-500 rounded-full"
                            style={{ width: `${(product.store_share_ty || 0) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Contribution with heat bar */}
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">{formatPercent(product.contribution_rev_pct, 1)}</div>
                        <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                          <div 
                            className="h-1 bg-blue-500 rounded-full"
                            style={{ width: `${(product.contribution_rev_pct || 0) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Dotcom $ */}
                  <td className="p-4">
                    <div className="font-medium text-foreground">{formatCurrency(product.dotcom_sales_ty)}</div>
                  </td>

                  {/* Store $ */}
                  <td className="p-4">
                    <div className="font-medium text-foreground">{formatCurrency(product.store_sales_ty)}</div>
                  </td>

                  {/* OOS % */}
                  <td className="p-4">
                    <div className={`font-medium ${
                      (product.oos_pct || 0) > 0.1 ? 'text-red-600' : 'text-foreground'
                    }`}>
                      {formatPercent(product.oos_pct)}
                    </div>
                  </td>

                  {/* Store Count */}
                  <td className="p-4">
                    <div className="font-medium text-foreground">{formatNumber(product.store_count)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Quality Footer */}
      {deepDiveData?.meta && (
        <div className="bg-muted/20 border border-border/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Data Quality</h4>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• SKUs analyzed: {products.length}</p>
            <p>• Total active stores: {deepDiveData.meta.totalActiveStores}</p>
            <p>• Week: {deepDiveData.week}</p>
            <p>• Data version: {deepDiveData.meta.data_version}</p>
          </div>
        </div>
      )}
    </div>
  )
}
